import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
  useMediaQuery,
  Chip,
  Avatar,
  Divider,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  People,
  Handshake,
  Mic,
  Campaign,
  PlayArrow,
  Star,
  EmojiEvents,
  Celebration,
  Casino,
  SportsEsports,
  TrendingUp,
  CheckCircle,
  ArrowForward,
} from "@mui/icons-material";
import CustomAppBar from "components/CustomHeader";
import Footer from "components/Footer";

const UseCasesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [hoveredCard, setHoveredCard] = useState(null);
  const [animatedBalls, setAnimatedBalls] = useState([]);

  // Generate smaller floating bingo balls animation
  useEffect(() => {
    const balls = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      number: Math.floor(Math.random() * 75) + 1,
      x: Math.random() * 100,
      y: Math.random() * 100,
      delay: Math.random() * 5,
      color: ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"][
        Math.floor(Math.random() * 6)
      ],
    }));
    setAnimatedBalls(balls);
  }, []);

  const useCases = [
    {
      icon: <People sx={{ fontSize: 32, color: "#FF6B6B" }} />,
      title: "Employee Engagement",
      subtitle: "Break the Monotony!",
      description:
        "Transform boring work days into exciting bingo adventures! Perfect for Monday blues, post-lunch energy dips, or whenever your team needs a fun boost.",
      features: [
        "Quick 15-min games",
        "Team bonding",
        "Instant mood lifter",
        "Remote-friendly",
      ],
      color: "#FF6B6B",
      stats: { 
        games: { label: "Games Played", value: "10K+", progress: 85 },
        happiness: { label: "Happiness Score", value: "98%", progress: 98 }
      },
      tags: ["Productivity", "Fun", "Engagement"],
      gradient: "linear-gradient(135deg, #FF6B6B20 0%, #FF6B6B10 100%)",
    },
    {
      icon: <Handshake sx={{ fontSize: 32, color: "#4ECDC4" }} />,
      title: "Onboarding & Icebreakers",
      subtitle: "Welcome to the Family!",
      description:
        "Make new hires feel like VIPs from day one! Our bingo games turn awkward introductions into memorable connections that last.",
      features: [
        "Custom welcome themes",
        "Get-to-know games",
        "Culture integration",
        "Instant connections",
      ],
      color: "#4ECDC4",
      stats: { 
        newHires: { label: "New Hires", value: "5K+", progress: 75 },
        satisfaction: { label: "Satisfaction", value: "96%", progress: 96 }
      },
      tags: ["Onboarding", "Culture", "Connection"],
      gradient: "linear-gradient(135deg, #4ECDC420 0%, #4ECDC410 100%)",
    },
    {
      icon: <Mic sx={{ fontSize: 32, color: "#45B7D1" }} />,
      title: "Conferences & Webinars",
      subtitle: "Keep Them Hooked!",
      description:
        "Say goodbye to sleepy audiences! Our interactive bingo keeps conference-goers engaged, alert, and having a blast throughout your event.",
      features: [
        "Live audience interaction",
        "Real-time engagement",
        "Presentation integration",
        "Prize competitions",
      ],
      color: "#45B7D1",
      stats: { 
        events: { label: "Events", value: "2K+", progress: 65 },
        engagement: { label: "Engagement", value: "94%", progress: 94 }
      },
      tags: ["Events", "Engagement", "Interactive"],
      gradient: "linear-gradient(135deg, #45B7D120 0%, #45B7D110 100%)",
    },
    {
      icon: <Campaign sx={{ fontSize: 32, color: "#96CEB4" }} />,
      title: "HR Campaigns & Awareness",
      subtitle: "Messages That Stick!",
      description:
        "Turn important company messages into fun, memorable experiences. Whether it's diversity, wellness, or safety - make it stick with bingo!",
      features: [
        "Custom campaign themes",
        "Educational content",
        "Behavior reinforcement",
        "Measurable results",
      ],
      color: "#96CEB4",
      stats: { 
        campaigns: { label: "Campaigns", value: "500+", progress: 70 },
        retention: { label: "Retention", value: "87%", progress: 87 }
      },
      tags: ["Education", "Awareness", "Impact"],
      gradient: "linear-gradient(135deg, #96CEB420 0%, #96CEB410 100%)",
    },
  ];

  const UseCaseCard = ({ useCase, index, isHovered, onHover, onLeave }) => (
    <Card
      onMouseEnter={() => onHover(index)}
      onMouseLeave={onLeave}
      sx={{
        height: "100%",
        borderRadius: 4,
        boxShadow: isHovered 
          ? "0 20px 40px rgba(0,0,0,0.1)" 
          : "0 8px 24px rgba(0,0,0,0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
        background: "#fff",
        border: `2px solid ${isHovered ? useCase.color : "transparent"}`,
        cursor: "pointer",
        "&:hover": {
          "& .use-case-avatar": {
            transform: "scale(1.1)",
          },
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        {/* Header with Avatar and Stats */}
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}>
          <Avatar
            className="use-case-avatar"
            sx={{
              width: 64,
              height: 64,
              background: useCase.gradient,
              border: `3px solid ${useCase.color}20`,
              mr: 3,
              transition: "transform 0.3s ease",
            }}
          >
            {useCase.icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="overline"
              sx={{
                color: useCase.color,
                fontWeight: "bold",
                letterSpacing: "1px",
                fontSize: "0.75rem",
              }}
            >
              Use Case {index + 1}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: "#1a1a1a",
                mb: 0.5,
                fontSize: isMobile ? "1.25rem" : "1.5rem",
              }}
            >
              {useCase.title}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: useCase.color,
                fontWeight: "600",
                fontSize: "1rem",
              }}
            >
              {useCase.subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        <Typography
          variant="body1"
          sx={{
            color: "#666",
            mb: 3,
            lineHeight: 1.6,
            fontSize: "0.95rem",
          }}
        >
          {useCase.description}
        </Typography>

        {/* Features Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#333",
              fontWeight: "bold",
              mb: 2,
              fontSize: "0.9rem",
            }}
          >
            Key Features
          </Typography>
          <Grid container spacing={1}>
            {useCase.features.map((feature, i) => (
              <Grid item xs={6} key={i}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <CheckCircle 
                    sx={{ 
                      color: useCase.color, 
                      fontSize: 16, 
                      mr: 1 
                    }} 
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#555",
                      fontSize: "0.85rem",
                      fontWeight: "500",
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Stats with Progress Bars */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: "#333",
              fontWeight: "bold",
              mb: 2,
              fontSize: "0.9rem",
            }}
          >
            Performance Metrics
          </Typography>
          {Object.entries(useCase.stats).map(([key, stat]) => (
            <Box key={key} sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontSize: "0.85rem" }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ 
                    color: useCase.color, 
                    fontWeight: "bold",
                    fontSize: "0.85rem",
                  }}
                >
                  {stat.value}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={stat.progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: `${useCase.color}20`,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: useCase.color,
                    borderRadius: 3,
                  },
                }}
              />
            </Box>
          ))}
        </Box>

        {/* Tags */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
          {useCase.tags.map((tag, i) => (
            <Chip
              key={i}
              label={tag}
              size="small"
              sx={{
                backgroundColor: `${useCase.color}15`,
                color: useCase.color,
                border: `1px solid ${useCase.color}30`,
                fontWeight: "600",
                fontSize: "0.75rem",
                "&:hover": {
                  backgroundColor: `${useCase.color}25`,
                },
              }}
            />
          ))}
        </Box>

        {/* Action Button */}
        <Button
          variant="contained"
          endIcon={<ArrowForward />}
          fullWidth
          sx={{
            backgroundColor: useCase.color,
            color: "white",
            fontWeight: "bold",
            py: 1.5,
            borderRadius: 3,
            textTransform: "none",
            fontSize: "0.95rem",
            boxShadow: `0 4px 14px ${useCase.color}40`,
            "&:hover": {
              backgroundColor: useCase.color,
              transform: "translateY(-2px)",
              boxShadow: `0 6px 20px ${useCase.color}50`,
            },
          }}
        >
          Explore This Use Case
        </Button>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ 
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
      position: "relative",
    }}>
      {/* Keep AppBar visible */}
      <CustomAppBar />

      {/* Smaller Floating Bingo Balls */}
      {animatedBalls.map((ball) => (
        <Box
          key={ball.id}
          sx={{
            position: "fixed",
            left: `${ball.x}%`,
            top: `${ball.y}%`,
            width: "24px", // Reduced from 40px
            height: "24px", // Reduced from 40px
            borderRadius: "50%",
            background: ball.color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: "bold",
            fontSize: "10px", // Reduced from 14px
            zIndex: 1,
            animation: `float 8s ease-in-out infinite ${ball.delay}s, drift 20s linear infinite`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            opacity: 0.8,
          }}
        >
          {ball.number}
        </Box>
      ))}

      {/* Main Content */}
      <Box sx={{ position: "relative", zIndex: 2, pt: 4 }}>
        {/* Hero Section */}
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Container maxWidth="lg">
            <Typography
              variant="h2"
              sx={{
                fontWeight: "bold",
                color: "#1a1a1a",
                mb: 2,
                fontSize: isMobile ? "2.5rem" : "3.5rem",
              }}
            >
              🎯 Bingo Use Cases
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: "#666",
                fontWeight: "500",
                mb: 3,
                fontSize: isMobile ? "1.2rem" : "1.5rem",
              }}
            >
              Where Fun Meets Purpose! 🎉
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#888",
                maxWidth: "700px",
                margin: "0 auto",
                lineHeight: 1.8,
                fontSize: "1.1rem",
              }}
            >
              Transform your workplace into an engaging environment with our 
              interactive bingo experiences that bring people together and 
              create memorable moments.
            </Typography>
          </Container>
        </Box>

        {/* Stats Overview */}
        <Box sx={{ py: 6 }}>
          <Container maxWidth="lg">
            <Paper
              elevation={3}
              sx={{
                borderRadius: 4,
                p: 4,
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Grid container spacing={4}>
                {[
                  { icon: <SportsEsports />, number: "50K+", label: "Games Played", color: "#FF6B6B" },
                  { icon: <People />, number: "1M+", label: "Players", color: "#4ECDC4" },
                  { icon: <EmojiEvents />, number: "25K+", label: "Prizes Won", color: "#45B7D1" },
                  { icon: <TrendingUp />, number: "99%", label: "Satisfaction", color: "#96CEB4" },
                ].map((stat, i) => (
                  <Grid item xs={6} md={3} key={i}>
                    <Box sx={{ textAlign: "center" }}>
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          backgroundColor: `${stat.color}20`,
                          mb: 2,
                          mx: "auto",
                        }}
                      >
                        {React.cloneElement(stat.icon, { 
                          sx: { color: stat.color, fontSize: 28 } 
                        })}
                      </Avatar>
                      <Typography
                        variant="h4"
                        sx={{
                          color: "#1a1a1a",
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#666",
                          fontWeight: "500",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Container>
        </Box>

        {/* Use Cases Grid */}
        <Box sx={{ py: 6 }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: "#1a1a1a",
                mb: 6,
              }}
            >
              Explore Our Use Cases
            </Typography>
            <Grid container spacing={4}>
              {useCases.map((useCase, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <UseCaseCard
                    useCase={useCase}
                    index={index}
                    isHovered={hoveredCard === index}
                    onHover={setHoveredCard}
                    onLeave={() => setHoveredCard(null)}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ py: 8 }}>
          <Container maxWidth="lg">
            <Paper
              elevation={6}
              sx={{
                borderRadius: 6,
                p: 6,
                textAlign: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  fontSize: isMobile ? "2rem" : "2.5rem",
                }}
              >
                Ready to Get Started? 🎮
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 4,
                  maxWidth: "500px",
                  margin: "0 auto 2rem",
                  opacity: 0.9,
                }}
              >
                Join thousands of organizations creating unforgettable 
                experiences with our bingo platform!
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Casino />}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  fontWeight: "bold",
                  py: 2,
                  px: 4,
                  borderRadius: 4,
                  fontSize: "1.1rem",
                  textTransform: "none",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                  backdropFilter: "blur(10px)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.3)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Start Your Bingo Journey
              </Button>
            </Paper>
          </Container>
        </Box>
      </Box>

      {/* Keep Footer visible */}
      <Footer />

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(180deg);
          }
        }
        @keyframes drift {
          0% {
            transform: translateX(-50px);
          }
          100% {
            transform: translateX(calc(100vw + 50px));
          }
        }
      `}</style>
    </Box>
  );
};

export default UseCasesPage;